import { useEffect, useState } from 'react'
import { Col, Form, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap'
import {
  ArrowClockwise,
  CheckCircleFill,
  PencilFill,
  PlusCircleFill,
  Search,
  TrashFill,
} from 'react-bootstrap-icons'
import { toast } from 'react-toastify'
import { Ask } from '../../../../components/Ask'
import { Center as CenterContent } from '../../../../components/Center'
import { CustomButton } from '../../../../components/CustomButton'
import { CustomSpinner } from '../../../../components/CustomSpinner'
import { useOrdenation } from '../../../../hooks/useOrdenation'
import WMSCodOperService from '../../../../services/database/WMSCodOperService'
import { areObjEqual } from '../../../../utils/obj'
import { sendErrorApiMessage } from '../../../../utils/sendErrorApiMessage'
import { ModalCreate } from './ModalCreate'
import { Center, Container, GroupX } from './styles'

export function Codigos() {
  const [codigos, setCodigos] = useState([])
  const [isCodigosLoading, setIsCodigosLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [isModalCreateOpen, setIsModalCreateOpen] = useState(false)

  const { filteredItems, Th, Paginate } = useOrdenation(codigos, 'id')

  async function handleSearch() {
    try {
      setIsCodigosLoading(true)

      const data = await WMSCodOperService.search({ search })

      setCodigos(data)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao pesquisar os códigos!')
    } finally {
      setIsCodigosLoading(false)
    }
  }

  async function loadCodigos() {
    try {
      setIsCodigosLoading(true)

      const data = await WMSCodOperService.getAll()

      setCodigos(data)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao carregar os códigos!')
    } finally {
      setIsCodigosLoading(false)
    }
  }

  useEffect(() => {
    loadCodigos()
  }, [])

  return (
    <>
      <Container>
        <h1>Códigos de Operação (codoper)</h1>

        <>
          <GroupX>
            <CustomButton
              title="Cadastrar Código"
              color="#0d9a04"
              onClick={() => setIsModalCreateOpen(true)}
            >
              <PlusCircleFill />
            </CustomButton>

            <CustomButton
              title="Recarregar"
              onClick={() => {
                setSearch('')
                loadCodigos()
              }}
            >
              <ArrowClockwise />
            </CustomButton>
          </GroupX>

          <CenterContent className="mt-4">
            <Form.Control
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Digite..."
              onKeyDown={(e) => {
                if (e.key === 'Enter' && search) {
                  handleSearch()
                }
              }}
            />

            <CustomButton
              title="Pesquisar"
              onClick={handleSearch}
              isDisabled={isCodigosLoading || !search}
            >
              <Search />
            </CustomButton>
          </CenterContent>

          {isCodigosLoading ? (
            <Center className="mt-3">
              <CustomSpinner size={48} />

              <h1>Carregando...</h1>
            </Center>
          ) : filteredItems.length === 0 ? (
            <p className="mt-3">Sem códigos</p>
          ) : (
            <>
              <Table>
                <thead>
                  <tr>
                    <Th
                      value="id"
                      label="ID"
                    />

                    <Th
                      value="descricao"
                      label="Descrição"
                    />

                    <Th label="Ações" />
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((codigo) => (
                    <Codigo
                      key={codigo.id}
                      codigo={codigo}
                      loadCodigos={loadCodigos}
                    />
                  ))}
                </tbody>
              </Table>

              <Paginate />
            </>
          )}
        </>
      </Container>

      {isModalCreateOpen && (
        <ModalCreate
          isOpen={isModalCreateOpen}
          onClose={() => setIsModalCreateOpen(false)}
          loadCodigos={loadCodigos}
        />
      )}
    </>
  )
}

function Codigo({ codigo, loadCodigos }) {
  const [data, setData] = useState(codigo)
  const [isDeleteRequestLoading, setIsDeleteRequestLoading] = useState(false)
  const [isUpdateRequestLoading, setIsUpdateRequestLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(true)

  function handleChange(e) {
    const { name, value } = e.target

    setData((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  async function handleUpdateCodigo(id) {
    try {
      setIsUpdateRequestLoading(true)

      await WMSCodOperService.update(id, data)

      loadCodigos()
      toast.success('Código atualizado com sucesso!')
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao atualizar o codigo!')
    } finally {
      setIsUpdateRequestLoading(false)
    }
  }

  async function handleDeleteTipo(id) {
    try {
      setIsDeleteRequestLoading(true)

      await WMSCodOperService.delete(id)

      loadCodigos()
      toast.success('Código deletado com sucesso!')
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao deletar o código!')
    } finally {
      setIsDeleteRequestLoading(false)
    }
  }

  return (
    <tr>
      <td>{data.id}</td>

      <td>
        <Row>
          <Col>
            <OverlayTrigger overlay={<Tooltip>{data?.descricao}</Tooltip>}>
              <Form.Control
                name="descricao"
                value={data?.descricao}
                onChange={handleChange}
                disabled={isEditMode}
              />
            </OverlayTrigger>
          </Col>

          <Col>
            <Form.Select
              name="codoper"
              value={data?.codoper}
              onChange={handleChange}
              disabled={isEditMode}
            >
              <option value="">Selecione</option>
              <option value="E">Entrada</option>
              <option value="S">Saída</option>
              <option value="E/S">Entrada/Saída</option>
            </Form.Select>
          </Col>
        </Row>
      </td>

      <td>
        <CenterContent>
          <OverlayTrigger overlay={<Tooltip>Editar</Tooltip>}>
            <CustomButton
              noMargin
              isLoading={!isEditMode && isUpdateRequestLoading}
              color={isEditMode ? 'orange' : 'green'}
              onClick={() =>
                isEditMode
                  ? setIsEditMode(false)
                  : areObjEqual(data, codigo)
                    ? setIsEditMode(true)
                    : handleUpdateCodigo(data.id)
              }
            >
              {isEditMode ? <PencilFill /> : <CheckCircleFill />}
            </CustomButton>
          </OverlayTrigger>

          <OverlayTrigger overlay={<Tooltip>Deletar</Tooltip>}>
            <CustomButton
              noMargin
              color="red"
              isLoading={isDeleteRequestLoading}
              onClick={() =>
                Ask('Deseja excluir o código?', () =>
                  handleDeleteTipo(codigo.id),
                )
              }
            >
              <TrashFill />
            </CustomButton>
          </OverlayTrigger>
        </CenterContent>
      </td>
    </tr>
  )
}
